/**
 * Category Model - Processes API responses for category data
 */

/**
 * Process category list response data
 * @param {Object} response - API response from category list endpoint
 * @returns {Object} Formatted category list data
 */
export const categoryListGet = (response) => {
  if (response && response.data && response.data.success === true) {
    // Map the API response to our frontend model
    const categories = Array.isArray(response.data.data) ? response.data.data.map(category => ({
      id: category.id || null,
      slack: category.slack || null,
      category_code: category.category_code || null,
      name: category.label || null,
      category_type: category.category_type || null,
      description: category.description || '',
      status: category.status ?? 0,
      parent_category_id: category.parent_category_id || null,
      show_on_pos_screen: category.display_on_pos_screen ?? 0,
      show_on_qr_menu: category.display_on_qr_menu ?? 0,
      status_data: category.status_data ? {
        id: category.status_data?.id || null,
        value: category.status_data?.value ?? null,
        label: category.status_data?.label || 'N/A',
        color: category.status_data?.color || null
      } : null,
      createdAt: category.createdAt || null,
      updatedAt: category.updatedAt || null,
      created_by: category.created_by || null,
      updated_by: category.updated_by || null,
      created_user: category.created_user ? {
        id: category.created_user?.id || null,
        fullname: category.created_user?.fullname || null
      } : null,
      updated_user: category.updated_user ? {
        id: category.updated_user?.id || null,
        fullname: category.updated_user?.fullname || null
      } : null,
      store: category?.store || {},
      active_product_count: category?.active_product_count ?? 0,
      kitchen_display_id: category.kitchen_display_id || null,
      kitchen_display: category.kitchen_display || category.KitchenDisplay || null,
      station_name: category.kitchen_display?.label || category.KitchenDisplay?.label || category.station_name || '',
      ticket_type: category.ticket_type || 'KOT',
      printer_id: category.printer_id || null,
      printer: category.printer || category.Printer || null,
      printer_name: category.printer?.printer_name || category.Printer?.printer_name || category.printer_name || ''
    })) : [];
    return {
      success: true,
      total: response.data.total || categories.length,
      data: categories
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch category list',
    data: []
  };
};

/**
 * Process single category details response
 * @param {Object} response - API response from category details endpoint
 * @returns {Object} Formatted category details
 */
export const categoryDetailsGet = (response) => {
  if (response && response.data && response.data.success === true) {
    const item = response.data.data || {};
    return {
      success: true,
      data: {
        id: item.id || null,
        slack: item.slack || null,
        category_code: item.category_code || null,
        name: item.label || null,
        category_type: item.category_type || null,
        description: item.description || '',
        status: item.status ?? 0,
        parent_category_id: item.parent_category_id || null,
        show_on_pos_screen: item.display_on_pos_screen ?? 0,
        show_on_qr_menu: item.display_on_qr_menu ?? 0,
        kitchen_display_id: item.kitchen_display_id || null,
        kitchen_display: item.kitchen_display || item.KitchenDisplay || null,
        station_name: item.kitchen_display?.label || item.KitchenDisplay?.label || item.station_name || '',
        ticket_type: item.ticket_type || 'KOT',
        printer_id: item.printer_id || null,
        printer: item.printer || item.Printer || null,
        printer_name: item.printer?.printer_name || item.Printer?.printer_name || item.printer_name || '',
        status_data: item.status_data ? {
          id: item.status_data?.id || null,
          value: item.status_data?.value ?? null,
          label: item.status_data?.label || 'N/A',
          color: item.status_data?.color || null
        } : null,
        createdAt: item.createdAt || null,
        updatedAt: item.updatedAt || null,
        created_by: item.created_by || null,
        updated_by: item.updated_by || null,
        created_user: item.created_user ? {
          id: item.created_user?.id || null,
          fullname: item.created_user?.fullname || null
        } : null,
        updated_user: item.updated_user ? {
          id: item.updated_user?.id || null,
          fullname: item.updated_user?.fullname || null
        } : null,
        store: item?.store || {},
        active_product_count: item?.active_product_count ?? 0
      }
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch category details',
    data: {}
  };
};

/**
 * Process category creation response
 * @param {Object} response - API response from category create endpoint
 * @returns {Object} Formatted creation response
 */
export const categoryCreatePost = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Category created successfully',
      data: response.data.data || null
    };
  }
  return {
    success: false,
    message: 'Failed to create category',
    data: null
  };
};

/**
 * Process category update response
 * @param {Object} response - API response from category update endpoint
 * @returns {Object} Formatted update response
 */
export const categoryUpdatePut = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Category updated successfully',
      data: response.data.data || null
    };
  }
  return {
    success: false,
    message: 'Failed to update category',
    data: null
  };
};

/**
 * Process category delete response
 * @param {Object} response - API response from category delete endpoint
 * @returns {Object} Formatted delete response
 */
export const categoryDeleteDel = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Category deleted successfully'
    };
  }
  return {
    success: false,
    message: 'Failed to delete category'
  };
};

/**
 * Process category status update response
 * @param {Object} response - API response from category status update endpoint
 * @returns {Object} Formatted status update response
 */
export const categoryStatusPut = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Category status updated successfully'
    };
  }
  return {
    success: false,
    message: 'Failed to update category status'
  };
};

/**
 * Process category export response
 * @param {Object} response - API response from category export endpoint
 * @returns {Object} Formatted export response
 */
export const categoryExportGet = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Categories exported successfully',
      data: response.data.data || null
    };
  }
  return {
    success: false,
    message: 'Failed to export categories',
    data: null
  };
};

/**
 * Process sync with PMS response
 * @param {Object} response - API response from sync with PMS endpoint
 * @returns {Object} Formatted sync response
 */
export const syncWithPmsPost = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Categories synced with PMS successfully',
      data: response.data.data || null
    };
  }
  return {
    success: false,
    message: 'Failed to sync categories with PMS',
    data: null
  };
};

